/**
 * IpoPipelinePropertyFilter — the Pipeline类型 filter for the Impact page's
 * Industry Pipeline Overview (IPO). Mirrors the Motion Pipeline tab's
 * PipelinePropertyFilter contract:
 *
 *   - One-level AND/OR Cloudscape PropertyFilter (`enableTokenGroups`)
 *   - Fields: SFDC_STAGE / PIPELINE_SCORE / CREATED_DATE / CLOSE_DATE /
 *     IS_BIG_BET / HAS_IVT_ACTIVITY (Impact-scoped superset)
 *   - Full-width row wrapped in the shared <FloatingLabelField> so the
 *     notched "Pipeline类型" label rides the PropertyFilter's top border,
 *     pixel-consistent with every other IVT filter cell.
 *
 * Presentational only — the parent owns query state, translation to the
 * backend param (queryToImpactFilterParam), and the caching/refetch cycle.
 * Candidate value pools are HARDCODED here (SFDC picklist literals, 0-3
 * score domain, true/false booleans) — see comment on IMPACT_FILTERING_OPTIONS
 * below for the rationale.
 */
import { PropertyFilter, type PropertyFilterProps } from '@amzn/awsui-components-react';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import {
  DATE_OPERATORS,
  ENUM_OPERATORS,
  GREENFIELD_OPERATORS,
  BOOLEAN_VALUES,
  IS_GREENFIELD_OPTIONS,
  HIERARCHY_OPERATORS,
  PROPERTY_FILTER_I18N,
  TAG_MATCHES_HIERARCHY_OPTIONS,
} from '../../../components/pipelineFilterShared';

// ── PropertyFilter config ──────────────────────────────────────────────────
// The candidate value pools (stages, scores, booleans) are HARDCODED rather
// than fetched from the backend (design doc §3.4):
//   - SFDC_STAGE: the fixed SFDC picklist literals V36/V43 views hardcode in
//     is_launched/is_active predicates. A new stage value would need a
//     backend change anyway (to appear in is_launched/is_active semantics),
//     so hardcoding here has the same maintenance shape.
//   - PIPELINE_SCORE: 0-3 today (the tagged_pipeline.max_score domain). Rubric
//     may widen — when it does, add values here in the same commit.
//   - IS_BIG_BET / HAS_IVT_ACTIVITY / IS_GREENFIELD / TAG_MATCHES_HIERARCHY: literal
//     true/false from the
//     shared module. Backend parses either case.
const SFDC_STAGE_VALUES = [
  'Prospect',
  'Qualified',
  'Technical Validation',
  'Business Validation',
  'Committed',
  'Launched',
  'Completed',
  'Closed Lost',
] as const;

const PIPELINE_SCORE_VALUES: readonly string[] = ['0', '1', '2', '3'];

const FILTERING_PROPERTIES: readonly PropertyFilterProps.FilteringProperty[] = [
  {
    key: 'SFDC_STAGE',
    propertyLabel: 'SFDC Stage',
    groupValuesLabel: 'SFDC Stage values',
    operators: ENUM_OPERATORS,
    defaultOperator: '=',
  },
  {
    key: 'PIPELINE_SCORE',
    propertyLabel: 'Engagement Depth',
    groupValuesLabel: 'Engagement Depth values',
    operators: ENUM_OPERATORS,
    defaultOperator: '=',
  },
  {
    key: 'CREATED_DATE',
    propertyLabel: 'Create Date',
    groupValuesLabel: 'Create Date values',
    // Date fields get scalar comparison operators for range queries + = / !=
    // for point-in-time equality. `tokenType: 'value'` renders Cloudscape's
    // default text input; the input is a date string (YYYY-MM-DD). Users
    // typing malformed dates will 400 at the backend parse boundary.
    operators: DATE_OPERATORS,
    defaultOperator: '>=',
  },
  {
    key: 'CLOSE_DATE',
    propertyLabel: 'Close Date',
    groupValuesLabel: 'Close Date values',
    operators: DATE_OPERATORS,
    defaultOperator: '>=',
  },
  {
    key: 'IS_BIG_BET',
    propertyLabel: 'Big Bet Pipelines',
    groupValuesLabel: 'Big Bet',
    operators: ENUM_OPERATORS,
    defaultOperator: '=',
  },
  {
    key: 'HAS_IVT_ACTIVITY',
    propertyLabel: 'IVT直接参与',
    groupValuesLabel: 'IVT参与 values',
    operators: ENUM_OPERATORS,
    defaultOperator: '=',
  },
  {
    key: 'IS_GREENFIELD',
    propertyLabel: 'Greenfield or Industry',
    groupValuesLabel: 'Greenfield or Industry values',
    operators: GREENFIELD_OPERATORS,
    defaultOperator: '=',
  },
  // Options name a SCOPE (Exclude Cross / Only Cross) so neither token can be misread as
  // "no filtering" — that state is "no token", not a value. See formatTagMatchesHierarchy.
  {
    key: 'TAG_MATCHES_HIERARCHY',
    propertyLabel: 'Exclude Cross-Industry Tagging',
    groupValuesLabel: 'Exclude Cross-Industry Tagging values',
    operators: HIERARCHY_OPERATORS,
    defaultOperator: '=',
  },
];

/** Static option pool for every enum field. Cloudscape renders these as the
 *  multi-select checkbox list under a token's operator dropdown. Date fields
 *  don't appear here — they use free-form value input. */
const FILTERING_OPTIONS: readonly PropertyFilterProps.FilteringOption[] = [
  ...SFDC_STAGE_VALUES.map((v) => ({ propertyKey: 'SFDC_STAGE', value: v })),
  ...PIPELINE_SCORE_VALUES.map((v) => ({ propertyKey: 'PIPELINE_SCORE', value: v, label: `${v} 分` })),
  ...BOOLEAN_VALUES.map((v) => ({ propertyKey: 'IS_BIG_BET', value: v })),
  ...BOOLEAN_VALUES.map((v) => ({ propertyKey: 'HAS_IVT_ACTIVITY', value: v })),
  ...IS_GREENFIELD_OPTIONS.map((o) => ({ propertyKey: 'IS_GREENFIELD', value: o.value, label: o.label })),
  ...TAG_MATCHES_HIERARCHY_OPTIONS.map((o) => ({ propertyKey: 'TAG_MATCHES_HIERARCHY', value: o.value, label: o.label })),
];

// Shared with every other IVT PropertyFilter — see PROPERTY_FILTER_I18N. Only
// filteringAriaLabel differs per control, so it is spread in here.
const I18N: PropertyFilterProps.I18nStrings = {
  ...PROPERTY_FILTER_I18N,
  filteringAriaLabel: 'Filter pipeline by attributes',
};

export interface IpoPipelinePropertyFilterProps {
  query: PropertyFilterProps.Query;
  onChange: (query: PropertyFilterProps.Query) => void;
}

export default function IpoPipelinePropertyFilter({
  query,
  onChange,
}: IpoPipelinePropertyFilterProps) {
  // Layout note: `.ivt-ipo__field` sizes this like every other filter cell
  // (flex share, min-width) so Pipeline分类 sits inline with Industry / Use
  // Case / Asset / Snapshot Date per Figma. The applied-token strip is
  // pulled out of the cell via CSS (see .ivt-ipo__pipeline-filter-cell rule)
  // so tokens overflow onto a second visual line inside the filter row,
  // matching Figma node 3186-43468.
  return (
    <FloatingLabelField
      label="Pipeline Property"
      className="ivt-ipo__field ivt-ipo__pipeline-filter-cell"
    >
      <PropertyFilter
        query={query}
        onChange={({ detail }) => onChange(detail)}
        filteringProperties={FILTERING_PROPERTIES}
        filteringOptions={FILTERING_OPTIONS}
        enableTokenGroups
        expandToViewport
        disableFreeTextFiltering
        filteringPlaceholder="Choose options"
        filteringAriaLabel="Filter pipeline by attributes"
        i18nStrings={I18N}
      />
    </FloatingLabelField>
  );
}
